/**
 * A `localStorage` wrapper that cannot throw.
 *
 * Guests arrive in Safari private mode, in the LINE in-app browser, and in
 * WebViews with storage disabled. Every one of those makes `localStorage`
 * throw on write — and on read, in some. Losing a cart is bad; a white screen
 * because a quota exception escaped a render is worse, so every path here
 * degrades to "no storage" instead.
 *
 * Everything is namespaced under `pokpok.web.` so a shared origin (a venue
 * landing page, a future back office on the same host) cannot collide.
 */

const PREFIX = 'pokpok.web.';

export function readJson<T>(key: string): T | null {
  try {
    const raw = globalThis.localStorage?.getItem(PREFIX + key);
    return raw === null || raw === undefined ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

export function writeJson(key: string, value: unknown): void {
  try {
    globalThis.localStorage?.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Full, disabled, or private. The surface keeps working from memory.
  }
}

export function remove(key: string): void {
  try {
    globalThis.localStorage?.removeItem(PREFIX + key);
  } catch {
    // As above.
  }
}

/**
 * Wipe everything a sitting left behind: the session, its cart, the submission
 * identity and the order history this phone accumulated.
 *
 * The sitting is over — the bill was paid and the table cleared, or the till
 * voided it — so the QR in this hand is spent (the server now answers this
 * session `unauthenticated`). Carrying any of it into the next scan is the class
 * of bug this exists to prevent: a stale cart, a stale order id, a spent
 * session that resumes onto a closed seating. `language` is deliberately kept —
 * it is a harmless display preference, not sitting data, and re-asking a guest
 * their language every time is a papercut with no upside. (owner, 2026-08-18.)
 */
export function clearGuestData(): void {
  for (const key of [
    StorageKeys.session,
    StorageKeys.cart,
    StorageKeys.cartOwner,
    StorageKeys.submission,
    StorageKeys.orders,
    StorageKeys.checkout,
  ]) {
    remove(key);
  }
}

export const StorageKeys = {
  /** The guest session, so a refresh or an app-switch does not re-scan the QR. */
  session: 'session',
  /** The cart, so a refresh mid-meal does not lose what was chosen. */
  cart: 'cart',
  /**
   * Which table+venue the persisted cart was built for, as `tenant::table`.
   *
   * The cart holds price-and-name snapshots of one venue's menu. A guest who
   * re-scans at a different table — or a different restaurant — must not carry
   * the old venue's lines into the new one (owner, 2026-08-17), so the cart is
   * discarded when this stops matching the active session.
   */
  cartOwner: 'cart-owner',
  /**
   * The order id and idempotency key minted for the current submit.
   *
   * Kept because they are what make a retry safe: the same `order_id` returns
   * the existing order rather than creating a second one, and the same
   * `Idempotency-Key` replays the original response. A guest who reloads
   * mid-submit must reuse both, not mint new ones.
   */
  submission: 'submission',
  /** Order ids this phone has submitted, so Q17 survives a refresh. */
  orders: 'orders',
  /**
   * The open ReservePay checkout attempt (Q8/Q9).
   *
   * This one is not a convenience. Paying **leaves the app entirely** — the tab
   * navigates to ReservePay's hosted page — so in-memory state is gone by the
   * time the guest comes back, and where they come back to (`/status`, fixed by
   * `POKPOK_GUEST_RETURN_URL`) carries no query parameter naming the checkout.
   * Without this record the surface cannot tell a guest who just paid from a
   * guest who never left, and it holds the `Idempotency-Key` that stops a retry
   * creating a second checkout at ReservePay for one bill.
   */
  checkout: 'checkout',
  /**
   * Customer bookings this phone created, keyed for BookDone / BookManage.
   *
   * The `manage_token` is served **once** by the create call, so it is written
   * here the instant it lands — it is the only credential for reading or
   * cancelling the booking afterwards. Deliberately **not** cleared by
   * `clearGuestData`: a booking is a future reservation reached from a booking
   * link, not data belonging to the QR sitting that just ended, and wiping it
   * when a table clears would strand a guest's own upcoming reservation.
   */
  bookings: 'bookings',
} as const;
